import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import type { OutboxItem } from '@Domain/Ports/Outbox/OutboxPort';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import outboxItemStateLabel from '@UI/Helpers/Outbox/outboxItemStateLabel';
import InterviewCaptureActions from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/InterviewActions/InterviewCaptureActions';
import OutboxItemCardActions from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/InterviewActions/OutboxItemCardActions';
import OrphanedInterviewStrip from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sections/PendingRecordingsSection/OrphanedInterviewStrip';
import type InternalInterviewListEntryView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewListEntryView';
import { css } from '@emotion/react';
import * as React from 'react';

const itemStyle = css`
	display: grid;
	grid-column: 1 / -1;
	grid-template-columns: subgrid;
	align-items: center;
	padding: 0.875rem 0;

	& + & {
		border-top: 1px solid ${theme.colors.border};
	}

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		row-gap: 0.5rem;
	}
`;

const infoStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.1875rem;
	align-items: flex-start;
	min-width: 0;
`;

const titleStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.large};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
`;

const dateStyle = css`
	margin: 0;
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const participantsStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

const outboxStateStyle = css`
	margin: 0;
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkFaint};
`;

type Props = {
	readonly entry: InternalInterviewListEntryView;
	readonly subject: SubjectAddress;
	readonly outboxItemsById: ReadonlyMap<string, OutboxItem>;
	readonly buildOnDiscarded: (localInterviewId: string) => () => void;
};

/**
 * One recording that exists on this device but is not yet safely on the server: a capture never carried
 * through (resume or delete) or a transfer that failed (retry or discard). A recording whose interview
 * was deleted meanwhile is headed by a strip. Exactly two cells on the list's grid: the recording's
 * description and transfer state, then its actions (an empty cell when it has none).
 */
const PendingRecordingItem: React.FC<Props> = ({ entry, subject, outboxItemsById, buildOnDiscarded }) => {
	const actions = React.useMemo<React.ReactNode>(() => {
		const resumableLocalInterviewId = entry.resumableCaptureLocalInterviewId;

		if (resumableLocalInterviewId !== null) {
			return (
				<InterviewCaptureActions
					subject={subject}
					localInterviewId={resumableLocalInterviewId}
					isNotes={entry.isNotes}
				/>
			);
		}

		const queueActionTarget = entry.queueActionTarget;

		if (queueActionTarget === null) {
			return null;
		}

		const outboxItem = outboxItemsById.get(queueActionTarget.outboxItemId);

		if (outboxItem === undefined || outboxItem.state !== 'failed') {
			return null;
		}

		return (
			<OutboxItemCardActions
				item={outboxItem}
				conductedOnDisplay={entry.conductedOnDisplay}
				isNotes={entry.isNotes}
				hasServerInterview={entry.hasServerInterview}
				onDiscarded={buildOnDiscarded(queueActionTarget.localInterviewId)}
			/>
		);
	}, [entry, subject, outboxItemsById, buildOnDiscarded]);

	return (
		<li css={itemStyle}>
			<div css={infoStyle}>
				<ConditionalLoader data={entry.outboxState === 'orphaned'}>
					<OrphanedInterviewStrip isNotes={entry.isNotes} />
				</ConditionalLoader>
				<span css={titleStyle}>{entry.title}</span>
				<ConditionalLoader data={entry.hasName}>
					<p css={dateStyle}>{entry.conductedOnDisplay}</p>
				</ConditionalLoader>
				<span css={participantsStyle}>{entry.participantsDisplay}</span>
				<ConditionalLoader data={entry.outboxState}>
					{(outboxState) => <p css={outboxStateStyle}>{outboxItemStateLabel(outboxState)}</p>}
				</ConditionalLoader>
			</div>
			<div>{actions}</div>
		</li>
	);
};

export default PendingRecordingItem;
